// CTAP2 Client PIN Protocol Implementation
// Implements authenticatorClientPIN (0x06) commands

import {encodeCBOR, parseCBOR, hexStringToByte} from './util';
import {
  generateECDHKeyPair,
  exportPublicKeyCOSE,
  importAuthenticatorPublicKey,
  deriveSharedSecret,
  encryptAES256CBC,
  decryptAES256CBC,
  sha256,
  padPin,
  leftPadPinHash,
  calculatePinUvAuthParam
} from './ctap2-crypto';

// Client PIN subcommands
const PIN_SUBCOMMAND = {
  GET_RETRIES: 0x01,
  GET_KEY_AGREEMENT: 0x02,
  SET_PIN: 0x03,
  CHANGE_PIN: 0x04,
  GET_PIN_TOKEN: 0x05,
  GET_PIN_UV_AUTH_TOKEN_USING_UV_WITH_PERMISSIONS: 0x06,
  GET_UV_RETRIES: 0x07,
  GET_PIN_UV_AUTH_TOKEN_USING_PIN_WITH_PERMISSIONS: 0x09
};

// PIN/UV Auth Token Permissions
const PIN_PERMISSIONS = {
  MAKE_CREDENTIAL: 0x01,  // mc
  GET_ASSERTION: 0x02,     // ga
  CREDENTIAL_MANAGEMENT: 0x04,  // cm
  BIO_ENROLLMENT: 0x08,    // be
  LARGE_BLOB_WRITE: 0x10,  // lbw
  AUTHENTICATOR_CONFIG: 0x20  // acfg
};

export {PIN_PERMISSIONS};

// ==================== Get PIN Retries ====================

/**
 * Get PIN retries count
 * @param {Function} transceive - APDU transceive function
 * @returns {Object} {retries, powerCycleState}
 */
export async function getPinRetries(transceive) {
  // Build CBOR: {1: 0x01}
  const cbor = encodeCBOR({
    1: PIN_SUBCOMMAND.GET_RETRIES
  });
  
  // Build APDU: 80 10 00 00 <len> 06 <cbor> 00 (Le)
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}06${cborHex}00`;
  
  const response = await transceive(apdu);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // Parse CBOR response
  const respBytes = hexStringToByte(response.slice(0, -4));
  if (respBytes[0] !== 0x00) {
    throw new Error(`CTAP error: 0x${respBytes[0].toString(16)}`);
  }
  
  const result = parseCBOR(respBytes.slice(1));
  return {
    retries: result[3] || 0,
    powerCycleState: result[4]
  };
}

// ==================== Get Key Agreement ====================

/**
 * Get authenticator's public key for ECDH
 * @param {Function} transceive - APDU transceive function
 * @returns {Object} COSE_Key
 */
export async function getKeyAgreement(transceive) {
  // Build CBOR map: {1: pinUvAuthProtocol, 2: subCommand}
  // IMPORTANT: JavaScript object keys are sorted numerically, so {1: x, 2: y} works correctly
  const cbor = encodeCBOR({
    1: 1,  // pinUvAuthProtocol version 1 (CP_REQ_PIN_UV_AUTH_PROTOCOL = 0x01)
    2: PIN_SUBCOMMAND.GET_KEY_AGREEMENT  // subCommand = 2 (CP_REQ_SUB_COMMAND = 0x02)
  });
  
  // Build APDU with Le (expected response length)
  // Format: CLA INS P1 P2 Lc Data Le
  // Le=00 means expect up to 256 bytes
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}06${cborHex}00`;
  
  const response = await transceive(apdu);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // Parse CBOR response
  const respBytes = hexStringToByte(response.slice(0, -4));
  
  if (respBytes[0] !== 0x00) {
    throw new Error(`CTAP error: 0x${respBytes[0].toString(16)}`);
  }
  
  const result = parseCBOR(respBytes.slice(1));
  return result[1]; // keyAgreement (COSE_Key)
}

// ==================== Set PIN ====================

/**
 * Set initial PIN
 * @param {Function} transceive - APDU transceive function
 * @param {string} newPin - New PIN (4-63 characters)
 */
export async function setPin(transceive, newPin) {
  if (newPin.length < 4 || newPin.length > 63) {
    throw new Error('PIN must be 4-63 characters');
  }
  
  // 1. Get authenticator's public key
  const authPublicKeyCOSE = await getKeyAgreement(transceive);
  
  // 2. Generate platform key pair
  const platformKeyPair = await generateECDHKeyPair();
  
  // 3. Import authenticator's public key
  const authPublicKey = await importAuthenticatorPublicKey(authPublicKeyCOSE);
  
  // 4. Derive shared secret
  const sharedSecret = await deriveSharedSecret(platformKeyPair.privateKey, authPublicKey);
  
  // 5. Pad PIN to 64 bytes
  const paddedPin = padPin(newPin);
  
  // 6. Encrypt PIN
  const newPinEnc = await encryptAES256CBC(sharedSecret, paddedPin);
  
  // 7. Calculate pinUvAuthParam
  const pinUvAuthParam = await calculatePinUvAuthParam(sharedSecret, newPinEnc);
  
  // 8. Export platform public key
  const platformPublicKeyCOSE = await exportPublicKeyCOSE(platformKeyPair.publicKey);
  
  // 9. Build CBOR request
  // Keys: 1=pinUvAuthProtocol, 2=subCommand, 3=keyAgreement, 4=pinUvAuthParam, 5=newPinEnc
  const cbor = encodeCBOR({
    1: 1,  // pinUvAuthProtocol (CP_REQ_PIN_UV_AUTH_PROTOCOL = 0x01)
    2: PIN_SUBCOMMAND.SET_PIN,  // subCommand (CP_REQ_SUB_COMMAND = 0x02)
    3: platformPublicKeyCOSE,  // keyAgreement (CP_REQ_KEY_AGREEMENT = 0x03)
    4: pinUvAuthParam,  // pinUvAuthParam (CP_REQ_PIN_UV_AUTH_PARAM = 0x04)
    5: newPinEnc  // newPinEnc (CP_REQ_NEW_PIN_ENC = 0x05)
  });
  
  // 10. Build APDU
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}06${cborHex}`;
  
  const response = await transceive(apdu);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // Parse response
  const respBytes = hexStringToByte(response.slice(0, -4));
  if (respBytes[0] !== 0x00) {
    const errorCodes = {
      0x31: 'PIN_INVALID',
      0x32: 'PIN_BLOCKED',
      0x33: 'PIN_AUTH_INVALID',
      0x34: 'PIN_AUTH_BLOCKED',
      0x35: 'PIN_NOT_SET'
    };
    const errorName = errorCodes[respBytes[0]] || `0x${respBytes[0].toString(16)}`;
    throw new Error(`CTAP error: ${errorName}`);
  }
  
  return true;
}

// ==================== Change PIN ====================

/**
 * Change existing PIN
 * @param {Function} transceive - APDU transceive function
 * @param {string} currentPin - Current PIN
 * @param {string} newPin - New PIN (4-63 characters)
 */
export async function changePin(transceive, currentPin, newPin) {
  if (currentPin.length < 4 || newPin.length < 4) {
    throw new Error('PIN must be at least 4 characters');
  }
  if (newPin.length > 63) {
    throw new Error('PIN must be at most 63 characters');
  }
  
  // 1. Get authenticator's public key
  const authPublicKeyCOSE = await getKeyAgreement(transceive);
  
  // 2. Generate platform key pair
  const platformKeyPair = await generateECDHKeyPair();
  
  // 3. Import authenticator's public key
  const authPublicKey = await importAuthenticatorPublicKey(authPublicKeyCOSE);
  
  // 4. Derive shared secret
  const sharedSecret = await deriveSharedSecret(platformKeyPair.privateKey, authPublicKey);
  
  // 5. Pad and encrypt new PIN
  const paddedNewPin = padPin(newPin);
  const newPinEnc = await encryptAES256CBC(sharedSecret, paddedNewPin);
  
  // 6. Hash and encrypt current PIN hash
  const currentPinHash = await sha256(new TextEncoder().encode(currentPin));
  const pinHashEnc = await encryptAES256CBC(sharedSecret, leftPadPinHash(currentPinHash));
  
  // 7. Calculate pinUvAuthParam over newPinEnc + pinHashEnc
  const combined = new Uint8Array(newPinEnc.length + pinHashEnc.length);
  combined.set(newPinEnc, 0);
  combined.set(pinHashEnc, newPinEnc.length);
  const pinUvAuthParam = await calculatePinUvAuthParam(sharedSecret, combined);
  
  // 8. Export platform public key
  const platformPublicKeyCOSE = await exportPublicKeyCOSE(platformKeyPair.publicKey);
  
  // 9. Build CBOR request
  // Keys: 1=pinUvAuthProtocol, 2=subCommand, 3=keyAgreement, 4=pinUvAuthParam, 5=newPinEnc, 6=pinHashEnc
  const cbor = encodeCBOR({
    1: 1,  // pinUvAuthProtocol (CP_REQ_PIN_UV_AUTH_PROTOCOL = 0x01)
    2: PIN_SUBCOMMAND.CHANGE_PIN,  // subCommand (CP_REQ_SUB_COMMAND = 0x02)
    3: platformPublicKeyCOSE,  // keyAgreement (CP_REQ_KEY_AGREEMENT = 0x03)
    4: pinUvAuthParam,  // pinUvAuthParam (CP_REQ_PIN_UV_AUTH_PARAM = 0x04)
    5: newPinEnc,  // newPinEnc (CP_REQ_NEW_PIN_ENC = 0x05)
    6: pinHashEnc  // pinHashEnc (CP_REQ_PIN_HASH_ENC = 0x06)
  });
  
  // 10. Build APDU
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}06${cborHex}`;
  
  const response = await transceive(apdu);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // Parse response
  const respBytes = hexStringToByte(response.slice(0, -4));
  if (respBytes[0] !== 0x00) {
    const errorCodes = {
      0x31: 'PIN_INVALID - Current PIN is incorrect',
      0x32: 'PIN_AUTH_BLOCKED',
      0x33: 'PIN_REQUIRED',
      0x34: 'PIN_NOT_SET'
    };
    const errorName = errorCodes[respBytes[0]] || `0x${respBytes[0].toString(16)}`;
    throw new Error(`CTAP error: ${errorName}`);
  }
  
  return true;
}

// ==================== Get PIN Token ====================

/**
 * Get PIN token for authenticating other operations
 * @param {Function} transceive - APDU transceive function
 * @param {string} pin - User's PIN
 * @returns {Uint8Array} PIN token (32 bytes)
 */
export async function getPinToken(transceive, pin) {
  if (pin.length < 4) {
    throw new Error('PIN must be at least 4 characters');
  }
  
  // 1. Get authenticator's public key
  const authPublicKeyCOSE = await getKeyAgreement(transceive);
  
  // 2. Generate platform key pair
  const platformKeyPair = await generateECDHKeyPair();
  
  // 3. Import authenticator's public key
  const authPublicKey = await importAuthenticatorPublicKey(authPublicKeyCOSE);
  
  // 4. Derive shared secret
  const sharedSecret = await deriveSharedSecret(platformKeyPair.privateKey, authPublicKey);
  
  // 5. Hash PIN
  const pinHash = await sha256(new TextEncoder().encode(pin));
  
  // 6. Encrypt PIN hash
  const pinHashEnc = await encryptAES256CBC(sharedSecret, leftPadPinHash(pinHash));
  
  // 7. Export platform public key
  const platformPublicKeyCOSE = await exportPublicKeyCOSE(platformKeyPair.publicKey);
  
  // 8. Build CBOR request
  // Keys: 1=pinUvAuthProtocol, 2=subCommand, 3=keyAgreement, 6=pinHashEnc
  const cbor = encodeCBOR({
    1: 1,  // pinUvAuthProtocol (CP_REQ_PIN_UV_AUTH_PROTOCOL = 0x01)
    2: PIN_SUBCOMMAND.GET_PIN_TOKEN,  // subCommand (CP_REQ_SUB_COMMAND = 0x02)
    3: platformPublicKeyCOSE,  // keyAgreement (CP_REQ_KEY_AGREEMENT = 0x03)
    6: pinHashEnc  // pinHashEnc (CP_REQ_PIN_HASH_ENC = 0x06)
  });
  
  // 9. Build APDU with Le (expected response)
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}06${cborHex}00`;
  
  const response = await transceive(apdu);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // Parse response
  const respBytes = hexStringToByte(response.slice(0, -4));
  if (respBytes[0] !== 0x00) {
    const errorCodes = {
      0x31: 'PIN_INVALID - Incorrect PIN',
      0x32: 'PIN_AUTH_BLOCKED - Too many failed attempts',
      0x33: 'PIN_REQUIRED',
      0x34: 'PIN_NOT_SET'
    };
    const errorName = errorCodes[respBytes[0]] || `0x${respBytes[0].toString(16)}`;
    throw new Error(`CTAP error: ${errorName}`);
  }
  
  const result = parseCBOR(respBytes.slice(1));
  const pinTokenEnc = result[2]; // Encrypted PIN token (32 bytes for Protocol 1)
  console.log('getPinToken: pinTokenEnc length:', pinTokenEnc?.length, 'type:', typeof pinTokenEnc);
  console.log('getPinToken: pinTokenEnc (hex):', Array.from(pinTokenEnc).map(b => b.toString(16).padStart(2, '0')).join(' '));
  console.log('getPinToken: sharedSecret length:', sharedSecret.length);
  console.log('getPinToken: sharedSecret (hex):', Array.from(sharedSecret).map(b => b.toString(16).padStart(2, '0')).join(' '));
  
  // 10. Decrypt PIN token using Protocol 1 (zero IV, no IV prepended)
  try {
    const pinToken = await decryptAES256CBC(sharedSecret, pinTokenEnc);
    console.log('getPinToken: Decrypted PIN token, length:', pinToken.length);
    return pinToken;
  } catch (error) {
    console.error('getPinToken: Decryption failed:', error);
    throw new Error(`Failed to decrypt PIN token: ${error.message}`);
  }
}

// ==================== Get PIN Token With Permissions ====================

/**
 * Get PIN UV Auth Token with specific permissions (CTAP2.1 0x09 subcommand)
 * 
 * @param {Function} transceive - APDU transceive function
 * @param {string} pin - User's PIN
 * @param {number} permissions - Bitmask of PIN_PERMISSIONS (e.g., PIN_PERMISSIONS.CREDENTIAL_MANAGEMENT)
 * @param {string|null} rpId - Optional RP ID (required for mc/ga permissions, forbidden for cm/be/lbw)
 * @returns {Uint8Array} PIN UV Auth Token (32 bytes for Protocol 1)
 */
export async function getPinTokenWithPermissions(transceive, pin, permissions, rpId = null) {
  // 1. Get device's public key
  const devicePublicKey = await getKeyAgreement(transceive);
  
  // 2. Generate platform ECDH key pair
  const {privateKey, publicKey: platformPublicKey} = await generateECDHKeyPair();
  
  // 3. Export platform public key as COSE_Key
  const platformPublicKeyCOSE = await exportPublicKeyCOSE(platformPublicKey);
  
  // 4. Import device's public key
  const devicePublicKeyCryptoKey = await importAuthenticatorPublicKey(devicePublicKey);
  
  // 5. Derive shared secret: SHA-256(ECDH(platform.private, device.public))
  const sharedSecret = await deriveSharedSecret(privateKey, devicePublicKeyCryptoKey);
  
  // 6. Hash PIN: SHA-256(PIN)
  const pinHash = await sha256(new TextEncoder().encode(pin));
  
  // 7. Pad PIN hash (left 16 bytes)
  const pinHashPadded = leftPadPinHash(pinHash);
  
  // 8. Encrypt PIN hash: AES-256-CBC(sharedSecret, pinHashPadded)
  const pinHashEnc = await encryptAES256CBC(sharedSecret, pinHashPadded);
  
  // 9. Build CBOR request
  // Keys: 1=pinUvAuthProtocol, 2=subCommand, 3=keyAgreement, 6=pinHashEnc, 9=permissions
  const cborData = {
    1: 1,  // pinUvAuthProtocol (CP_REQ_PIN_UV_AUTH_PROTOCOL = 0x01)
    2: PIN_SUBCOMMAND.GET_PIN_UV_AUTH_TOKEN_USING_PIN_WITH_PERMISSIONS,  // subCommand (CP_REQ_SUB_COMMAND = 0x02)
    3: platformPublicKeyCOSE,  // keyAgreement (CP_REQ_KEY_AGREEMENT = 0x03)
    6: pinHashEnc,  // pinHashEnc (CP_REQ_PIN_HASH_ENC = 0x06)
    9: permissions  // permissions (CP_REQ_PERMISSIONS = 0x09)
  };
  
  // Add RP ID if provided (required for mc/ga, forbidden for cm/be/lbw)
  if (rpId) {
    cborData[10] = rpId;  // rpId (CP_REQ_RP_ID = 0x0A)
  }
  
  const cbor = encodeCBOR(cborData);
  
  // 10. Build APDU: CLA INS P1 P2 Lc 06 CBOR Le
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}06${cborHex}00`;  // Added 00 for Le
  
  const response = await transceive(apdu);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // 11. Parse response
  const respBytes = hexStringToByte(response.slice(0, -4));
  if (respBytes[0] !== 0x00) {
    const errorCodes = {
      0x31: 'PIN_INVALID - Wrong PIN',
      0x32: 'PIN_AUTH_BLOCKED - Too many failed PIN attempts',
      0x33: 'PIN_AUTH_INVALID - Invalid pinUvAuthParam',
      0x36: 'PIN_REQUIRED - PIN not set',
      0x01: 'INVALID_COMMAND',
      0x02: 'INVALID_PARAMETER'
    };
    const errorMsg = errorCodes[respBytes[0]] || `Unknown error 0x${respBytes[0].toString(16)}`;
    throw new Error(`CTAP error: ${errorMsg}`);
  }
  
  const result = parseCBOR(respBytes.slice(1));
  const pinTokenEnc = result[2]; // Encrypted PIN token (32 bytes for Protocol 1)
  
  // 12. Decrypt PIN token using Protocol 1 (zero IV, no IV prepended)
  const pinToken = await decryptAES256CBC(sharedSecret, pinTokenEnc);
  
  return pinToken;
}

